import { ComponentFactoryResolver, ComponentRef, Directive, ElementRef, Input, Renderer2, ViewContainerRef } from '@angular/core';

/**
 * Permet d'afficher un composant sous forme de tooltip au survol par la souris.
 */
@Directive({
    selector: '[hubComponentTooltip]',
    standalone: true,
})
export class ComponentToolTipDirective {
    /**
     * Composant utilisé pour afficher la tooltip
     */
    @Input('hubComponentTooltip') component: any;

    /**
     * Position de la tooltip.
     */
    @Input()
    tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

    /**
     * Données de la tooltip.
     */
    @Input()
    tooltipData: any;

    /**
     * Element Html contenant la tooltip.
     */
    private popupComponentRef: ComponentRef<any> | null = null;

    /**
     * Valeur indiquant si la tooltip est affichée.
     */
    private tooltipVisible: boolean = false;

    /**
     * Une reference à l'instruction setTimeout utilisé pour fermer la tooltip.
     */
    private hideTimeoutRef: any = null;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private viewContainerRef: ViewContainerRef,
        private componentFactoryResolver: ComponentFactoryResolver
    ) {
        this.el.nativeElement.addEventListener('mouseenter', this.createTooltip.bind(this));
        this.el.nativeElement.addEventListener('mouseleave', this.hidePopup.bind(this));
    }

    /**
     * Crée la tooltip.
     */
    private createTooltip() {
        if (this.component && !this.popupComponentRef) {
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.component);
            this.popupComponentRef = this.viewContainerRef.createComponent(componentFactory);

            if (this.popupComponentRef) {
                this.popupComponentRef.setInput('data', this.tooltipData);

                const hostElem = this.el.nativeElement;
                const popupElem = this.popupComponentRef.location.nativeElement;

                this.renderer.listen(popupElem, 'mouseenter', () => {
                    this.keepTooltipVisible(); // Garder le tooltip visible
                });

                this.renderer.listen(popupElem, 'mouseleave', () => {
                    this.hidePopup();
                });

                setTimeout(() => this.displayTooltip(popupElem, hostElem), 10);
            }
        }
    }

    private displayTooltip(popupElem: any, hostElem: any) {
        popupElem.style.position = 'fixed';
        popupElem.style.display = 'block';

        /* Gestion de l'emplacement où est affiché la tooltip */
        const tooltipHeight = popupElem.scrollHeight;
        const tooltipWidth = popupElem.scrollWidth;

        let tooltipTop: number;
        let tooltipLeft: number;

        const { left, top, width, height } = hostElem.getBoundingClientRect();

        // Positionnement selon la position spécifiée
        switch (this.tooltipPosition) {
            case 'top':
                tooltipTop = top - tooltipHeight - 5; // Positionner au-dessus
                tooltipLeft = left + width / 2 - tooltipWidth / 2;
                break;
            case 'bottom':
                tooltipTop = top + height + 5; // Positionner en dessous
                tooltipLeft = left + width / 2 - tooltipWidth / 2;
                break;
            case 'left':
                tooltipTop = top + height / 2 - tooltipHeight / 2; // Centrer verticalement
                tooltipLeft = left - tooltipWidth - 5; // Positionner à gauche
                break;
            case 'right':
                tooltipTop = top + height / 2 - tooltipHeight / 2; // Centrer verticalement
                tooltipLeft = left + width + 5; // Positionner à droite
                break;
            default:
                tooltipTop = top - tooltipHeight - 5; // Fallback par défaut
                tooltipLeft = left + width / 2 - tooltipWidth / 2;
        }

        // Vérifier les débordements
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        // Ajustements pour éviter les débordements
        if (tooltipTop < 0) {
            tooltipTop = top + height + 5; // Revenir en dessous si débordement
        }
        if (tooltipTop + tooltipHeight > windowHeight) {
            tooltipTop = top - tooltipHeight - 5; // Revenir au-dessus si débordement
        }
        if (tooltipLeft < 0) {
            tooltipLeft = 5; // Assurer un espacement
        }
        if (tooltipLeft + tooltipWidth > windowWidth) {
            tooltipLeft = windowWidth - tooltipWidth - 5; // Ajuster à gauche
        }

        popupElem.style.left = `${tooltipLeft}px`;
        popupElem.style.top = `${tooltipTop}px`;
    }

    /**
     * Garde la tooltip visible.
     */
    private keepTooltipVisible() {
        if (this.hideTimeoutRef) {
            // Annule la fermeture de la tooltip.
            clearTimeout(this.hideTimeoutRef);
        }
    }

    /**
     * Masque la tooltip.
     */
    private hidePopup() {
        if (this.popupComponentRef) {
            this.hideTimeoutRef = setTimeout(() => {
                this.popupComponentRef.destroy();
                this.popupComponentRef = null;
                this.tooltipVisible = false; // Réinitialiser l'état de visibilité
            }, 10); // Correspond à la durée de la transition
        }
    }

    /**
     * Appellée lorsque la directive est détruite.
     */
    ngOnDestroy() {
        this.hidePopup();
        this.el.nativeElement.removeEventListener('mouseenter', this.createTooltip.bind(this));
        this.el.nativeElement.removeEventListener('mouseleave', this.hidePopup.bind(this));
    }
}
